// Owns browser decoding and the shared pixel renderer used by previews and exports.
import { cropWindow, outputSize, filterPixels, MEDIA_LIMITS } from '/media-policy.mjs';
/** Read processed blobs as data URLs; reject browser read failures. */
export const blobData = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read processed media.'));
    reader.readAsDataURL(blob);
  });
/** Decode oriented photos with an image-element fallback; always release temporary URLs. */
export async function decodePhoto(blob) {
  try {
    return await createImageBitmap(blob, { imageOrientation: 'from-image' });
  } catch {
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    } catch {
      throw new Error(
        'This browser cannot decode that photo. For HEIC/HEIF, try current Safari or export JPEG from Photos.',
      );
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}
/** One renderer for photo preview, photo export and video/reel frames. */
export function renderFrame(
  canvas,
  entry,
  { edge = MEDIA_LIMITS.edge, title = '', ratio, zoomExtra = 0 } = {},
) {
  const source = entry.source;
  const width = entry.kind === 'video' ? source.videoWidth : source.width || source.naturalWidth,
    height = entry.kind === 'video' ? source.videoHeight : source.height || source.naturalHeight;
  const quarter = entry.rotation % 180 !== 0,
    rotatedWidth = quarter ? height : width,
    rotatedHeight = quarter ? width : height;
  const crop = cropWindow(
      rotatedWidth,
      rotatedHeight,
      ratio ?? entry.ratio,
      entry.zoom + zoomExtra,
      entry.panX,
      entry.panY,
    ),
    size = outputSize(crop.width, crop.height, edge);
  if (canvas.width !== size.width) canvas.width = size.width;
  if (canvas.height !== size.height) canvas.height = size.height;
  const ctx = canvas.getContext('2d', {
    willReadFrequently: entry.filter !== 'original',
  });
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, size.width, size.height);
  ctx.save();
  ctx.scale(size.width / crop.width, size.height / crop.height);
  ctx.translate(-crop.x, -crop.y);
  ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
  ctx.rotate((entry.rotation * Math.PI) / 180);
  ctx.drawImage(source, -width / 2, -height / 2);
  ctx.restore();
  if (entry.filter !== 'original') {
    const pixels = ctx.getImageData(0, 0, size.width, size.height);
    filterPixels(pixels.data, entry.filter);
    ctx.putImageData(pixels, 0, 0);
  }
  if (title) {
    const font = Math.max(16, Math.round(size.width * 0.048));
    ctx.font = `600 ${font}px -apple-system, Helvetica, sans-serif`;
    ctx.textAlign = 'center';
    const words = title.split(/\s+/),
      lines = [];
    let line = '';
    for (const word of words) {
      const next = (line + ' ' + word).trim();
      if (ctx.measureText(next).width > size.width * 0.84 && line) {
        lines.push(line);
        line = word;
      } else line = next;
    }
    if (line) lines.push(line);
    const shown = lines.slice(0, 3),
      boxHeight = (shown.length + 1) * font * 1.35,
      top = size.height - boxHeight - size.height * 0.12;
    ctx.fillStyle = '#000a';
    ctx.fillRect(size.width * 0.05, top, size.width * 0.9, boxHeight);
    ctx.fillStyle = '#fff';
    shown.forEach((text, i) =>
      ctx.fillText(text, size.width / 2, top + font * 1.7 + i * font * 1.35),
    );
  }
  return size;
}
